import TaskStatusEnum from "@/enum/TaskStatus";
import {
    exportPurchasingLabelExcel,
    getExportPurchasingLabelResult,
} from "@/services/api/moreFeatures/purchasingLabel/purchasingLabelStatistics";
import { useRequest } from "ahooks";
import { Button, ButtonProps } from "antd";
import React, { useEffect, useRef, useState } from "react";

interface ExportPurchasingLabelExcelButtonProps extends ButtonProps {
    distributedId: number;
    onSuccess?: (downloadUrl: string) => void;
    onError?: () => void;
}

const ExportPurchasingLabelExcelButton: React.FC<ExportPurchasingLabelExcelButtonProps> = function (props) {
    const { distributedId, onSuccess, onError, ...rest } = props;

    const [exporting, updateExporting] = useState(false);

    const handledPollingResultRef = useRef(false);
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;

    const {
        runAsync: pollResult,
        cancel: cancelPolling,
        data: pollingResult,
        error: pollingError,
    } = useRequest(() => getExportPurchasingLabelResult(distributedId), {
        pollingInterval: 2000,
        manual: true,
        onError() {
            updateExporting(false);
        },
    });

    useEffect(() => {
        if (!pollingResult || ![TaskStatusEnum.COMPLETED, TaskStatusEnum.ERROR].includes(pollingResult.status)) {
            return;
        }

        cancelPolling();
        updateExporting(false);

        if (handledPollingResultRef.current) {
            return;
        }

        handledPollingResultRef.current = true;

        if (pollingResult.status === TaskStatusEnum.COMPLETED) {
            onSuccessRef.current?.(pollingResult.downloadUrl);
        } else {
            onErrorRef.current?.();
        }
    }, [pollingResult, cancelPolling]);

    useEffect(() => {
        if (!pollingError) return;

        cancelPolling();
        updateExporting(false);

        if (handledPollingResultRef.current) {
            return;
        }

        handledPollingResultRef.current = true;
        onErrorRef.current?.();
    }, [pollingError, cancelPolling]);

    const { runAsync: exportExcel } = useRequest(
        async () => {
            handledPollingResultRef.current = false;
            updateExporting(true);
            await exportPurchasingLabelExcel(distributedId);
            pollResult();
        },
        {
            manual: true,
            onError() {
                updateExporting(false);
                onError?.();
            },
        },
    );

    return (
        <Button
            {...rest}
            loading={exporting}
            onClick={(e) => {
                rest.onClick?.(e);
                exportExcel();
            }}
        >
            导出Excel
        </Button>
    );
};

export default React.memo(ExportPurchasingLabelExcelButton);
